import { FiltroEstadoVenta } from '@/components/filtros/FiltroEstadoVenta';
import { FiltroPeriodos } from '@/components/filtros/FiltroPeriodos';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useEstadosVenta, usePeriodosDisponibles } from '@/hooks/useAreaCosteo';
import { useFiltrosGlobales } from '@/lib/FiltrosGlobalesContext';
import { Funnel, MagnifyingGlass, X } from '@phosphor-icons/react';
import { useLocation } from 'react-router-dom';

/**
 * Qué filtros ESPECIALIZADOS aplica cada pantalla (auditado contra el backend).
 * Los estructurales (Proyecto/Bloque/Obra/Modelo/Área/Períodos) aplican en todas,
 * así que no se listan. Ruta desconocida → se asume que sí aplican (no atenuar).
 */
const SOPORTE_FILTROS: Record<string, { estado: boolean; avance: boolean }> = {
  '/costos': { estado: false, avance: false },
  '/costos-mensuales': { estado: false, avance: false },
  '/pivot': { estado: false, avance: false },
  '/avance': { estado: true, avance: true },
  '/costos-m2': { estado: true, avance: true },
  '/mensual': { estado: true, avance: false },
  '/venta-vs-costo': { estado: true, avance: false },
  '/vivienda-gerencia': { estado: true, avance: false },
  '/productividad': { estado: true, avance: true },
};

/**
 * Barra de filtros globales que aparece debajo del header en todas las vistas BI.
 *
 * Filtros disponibles:
 * - Búsqueda texto Obra (LIKE %texto%): nomenclatura parcial ej. "VB-1"
 * - Periodos: selector jerárquico Año/Mes/Día multi-select (no contiguo)
 * - Área Costeo (multi-select inline simple; en futuro: dropdown con catálogo
 *   dinámico desde DB)
 * - % Avance min/max
 * - Origen PROD/SBX
 *
 * Persistencia: estado en localStorage (ver FiltrosGlobalesContext).
 */
export function BarraFiltrosGlobales() {
  const { filtros, actualizar, limpiar } = useFiltrosGlobales();
  const { pathname } = useLocation();
  const soporte = SOPORTE_FILTROS[pathname] ?? { estado: true, avance: true };
  // Estilo/tooltip para un filtro que no aplica en la pantalla actual: se atenúa
  // y deshabilita, pero NO se oculta (layout estable + tooltip que educa).
  const clase = (ok: boolean) =>
    `flex flex-col gap-1 ${ok ? '' : 'opacity-40 pointer-events-none'}`;
  const titulo = (ok: boolean) => (ok ? undefined : 'No aplica en esta pantalla');
  const { data: periodosData } = usePeriodosDisponibles();
  const fechasDisponibles = periodosData?.data ?? [];
  const { data: estadosData } = useEstadosVenta();
  const estadosDisponibles = estadosData?.data ?? [];

  const algunActivo =
    !!filtros.worksNos?.length ||
    !!filtros.areasCosteo?.length ||
    !!filtros.categoriasTarea?.length ||
    !!filtros.aniosSel?.length ||
    !!filtros.mesesSel?.length ||
    !!filtros.diasSel?.length ||
    !!filtros.estadosSel?.length ||
    !!filtros.origen ||
    filtros.pctAvanceMin !== undefined ||
    filtros.pctAvanceMax !== undefined ||
    !!filtros.filtroObra ||
    !!filtros.filtroProyecto ||
    !!filtros.filtroBloque ||
    !!filtros.filtroModelo;

  return (
    <div className="border-b border-zinc-200 bg-white">
      <div className="container mx-auto max-w-7xl px-6 py-3">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
            <Funnel className="h-3.5 w-3.5" weight="bold" aria-hidden="true" />
            Filtros
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="filtroProyecto" className="text-xs flex items-center gap-1">
              <MagnifyingGlass className="h-3 w-3" weight="bold" />
              Proyecto
            </Label>
            <Input
              id="filtroProyecto"
              type="text"
              value={filtros.filtroProyecto ?? ''}
              onChange={(e) => actualizar({ filtroProyecto: e.target.value || undefined })}
              placeholder="VB, VN, F"
              className="h-8 w-24 text-sm"
            />
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="filtroBloque" className="text-xs flex items-center gap-1">
              <MagnifyingGlass className="h-3 w-3" weight="bold" />
              Bloque
            </Label>
            <Input
              id="filtroBloque"
              type="text"
              value={filtros.filtroBloque ?? ''}
              onChange={(e) => actualizar({ filtroBloque: e.target.value || undefined })}
              placeholder="1, 2, M"
              className="h-8 w-20 text-sm"
            />
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="filtroObra" className="text-xs flex items-center gap-1">
              <MagnifyingGlass className="h-3 w-3" weight="bold" />
              Obra
            </Label>
            <Input
              id="filtroObra"
              type="text"
              value={filtros.filtroObra ?? ''}
              onChange={(e) => actualizar({ filtroObra: e.target.value || undefined })}
              placeholder="VB-1.01, VN-M"
              className="h-8 w-36 text-sm"
            />
          </div>

          <div
            className="flex flex-col gap-1"
            title="Filtra obras por modelo de vivienda (LIKE). Ej: 'Santorini' incluye todas sus variantes."
          >
            <Label htmlFor="filtroModelo" className="text-xs flex items-center gap-1">
              <MagnifyingGlass className="h-3 w-3" weight="bold" />
              Modelo
            </Label>
            <Input
              id="filtroModelo"
              type="text"
              value={filtros.filtroModelo ?? ''}
              onChange={(e) => actualizar({ filtroModelo: e.target.value || undefined })}
              placeholder="Santorini, Ikaria…"
              className="h-8 w-32 text-sm"
            />
          </div>

          <div className="flex flex-col gap-1">
            <Label className="text-xs">Periodos</Label>
            <FiltroPeriodos
              fechasDisponibles={fechasDisponibles}
              aniosSel={filtros.aniosSel ?? []}
              mesesSel={filtros.mesesSel ?? []}
              diasSel={filtros.diasSel ?? []}
              onChange={(sel) =>
                actualizar({
                  aniosSel: sel.aniosSel.length ? sel.aniosSel : undefined,
                  mesesSel: sel.mesesSel.length ? sel.mesesSel : undefined,
                  diasSel: sel.diasSel.length ? sel.diasSel : undefined,
                })
              }
            />
          </div>

          <div className={clase(soporte.estado)} title={titulo(soporte.estado)}>
            <Label className="text-xs">Estado venta</Label>
            <FiltroEstadoVenta
              disponibles={estadosDisponibles}
              seleccion={filtros.estadosSel ?? []}
              onChange={(estados) =>
                actualizar({ estadosSel: estados.length ? estados : undefined })
              }
            />
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="areaCosteo" className="text-xs">
              Área Costeo
            </Label>
            <select
              id="areaCosteo"
              value={filtros.areasCosteo?.[0] ?? ''}
              onChange={(e) =>
                actualizar({
                  areasCosteo: e.target.value ? [e.target.value] : undefined,
                })
              }
              className="h-8 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="">(todas)</option>
              <option value="PRO VIVIENDA">PRO VIVIENDA</option>
              <option value="PRO INFRA">PRO INFRA</option>
              <option value="PRO COMERCIAL">PRO COMERCIAL</option>
            </select>
          </div>

          <div className={clase(soporte.avance)} title={titulo(soporte.avance)}>
            <Label htmlFor="pctAvanceMin" className="text-xs">
              % Avance min
            </Label>
            <Input
              id="pctAvanceMin"
              type="number"
              min={0}
              max={100}
              step={5}
              value={filtros.pctAvanceMin ?? ''}
              onChange={(e) =>
                actualizar({
                  pctAvanceMin: e.target.value === '' ? undefined : Number(e.target.value),
                })
              }
              className="h-8 w-20 text-sm"
              placeholder="0"
            />
          </div>

          <div className={clase(soporte.avance)} title={titulo(soporte.avance)}>
            <Label htmlFor="pctAvanceMax" className="text-xs">
              % Avance max
            </Label>
            <Input
              id="pctAvanceMax"
              type="number"
              min={0}
              max={100}
              step={5}
              value={filtros.pctAvanceMax ?? ''}
              onChange={(e) =>
                actualizar({
                  pctAvanceMax: e.target.value === '' ? undefined : Number(e.target.value),
                })
              }
              className="h-8 w-20 text-sm"
              placeholder="100"
            />
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="origen" className="text-xs">
              Origen
            </Label>
            <select
              id="origen"
              value={filtros.origen ?? ''}
              onChange={(e) =>
                actualizar({
                  origen: (e.target.value || undefined) as 'PRODUCTION' | 'SANDBOX' | undefined,
                })
              }
              className="h-8 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="">(ambos)</option>
              <option value="PRODUCTION">PRODUCTION</option>
              <option value="SANDBOX">SANDBOX</option>
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="incluirSSCC" className="text-xs">
              SSCC
            </Label>
            <label
              htmlFor="incluirSSCC"
              className="flex h-8 items-center gap-1.5 text-xs cursor-pointer select-none"
              title="Incluir Gasto Transitorio SSCC (Servicios Centrales) en totales. Por default se excluye (igual que PowerBI). No afecta el Drawer Detalle de Obra."
            >
              <input
                id="incluirSSCC"
                type="checkbox"
                checked={filtros.incluirSSCC === true}
                onChange={(e) => actualizar({ incluirSSCC: e.target.checked ? true : undefined })}
                className="h-3.5 w-3.5"
              />
              Incluir
            </label>
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="excluirIndirectos" className="text-xs">
              Indirectos
            </Label>
            <label
              htmlFor="excluirIndirectos"
              className="flex h-8 items-center gap-1.5 text-xs cursor-pointer select-none"
              title="Excluir partidas de Costo Indirecto (CI.*) de los totales. Útil para ver solo costo directo de construcción."
            >
              <input
                id="excluirIndirectos"
                type="checkbox"
                checked={filtros.excluirIndirectos === true}
                onChange={(e) =>
                  actualizar({ excluirIndirectos: e.target.checked ? true : undefined })
                }
                className="h-3.5 w-3.5"
              />
              Excluir
            </label>
          </div>

          {algunActivo && (
            <Button variant="ghost" size="sm" onClick={limpiar} className="h-8 gap-1 text-xs">
              <X className="h-3 w-3" weight="bold" />
              Limpiar
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
